import React, { useEffect, useState, useRef } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Eye, EyeOff } from "lucide-react";

const CATEGORIES = [
  { v: "director_general", l: "Dirección General" },
  { v: "socio", l: "Socios" },
  { v: "consejero", l: "Consejero" },
  { v: "instructor", l: "Instructor" },
  { v: "staff", l: "Staff" },
];

const EMPTY = {
  name: "", role_title: "", role_category: "socio",
  short_bio: "", full_bio: "", photo_url: "",
  initials_color_hex: "#F26A21", order: 100,
  is_public: true, linkedin_url: "", internal_phone: "", internal_email: "",
  needs_bio_update: false,
};

export default function StaffEquipo() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/team-members").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setCreating(true); };
  const openEdit = (m) => { setEditing({ ...EMPTY, ...m }); setCreating(false); };
  const closeModal = () => { setEditing(null); setCreating(false); };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar a ${m.name}?`)) return;
    try {
      await api.delete(`/admin/team-members/${m.id}`);
      toast.success("Miembro eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Equipo</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Equipo directivo y socios</h1>
            <p className="mt-1 text-sm text-gray-400">Gestiona los perfiles públicos del equipo DCC.</p>
          </div>
          <button onClick={openNew} data-testid="new-team-member-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nuevo miembro
          </button>
        </div>

        <div className="mt-8 overflow-x-auto border border-gray-800 rounded-xl" data-testid="team-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Puesto</th>
                <th className="text-left px-3 py-2">Categoría</th>
                <th className="text-left px-3 py-2">Público</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-gray-500">Sin miembros.</td></tr>
              )}
              {items.map((m) => (
                <tr key={m.id} className="border-t border-gray-800" data-testid={`team-row-${m.slug}`}>
                  <td className="px-3 py-2 font-mono text-gray-400">{m.order}</td>
                  <td className="px-3 py-2 text-white">
                    {m.name}
                    {m.needs_bio_update && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">Bio pendiente</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-300">{m.role_title}</td>
                  <td className="px-3 py-2 text-xs text-gray-400 uppercase">{m.role_category}</td>
                  <td className="px-3 py-2">
                    {m.is_public
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><Eye className="w-3 h-3" /> Público</span>
                      : <span className="inline-flex items-center gap-1 text-gray-500 text-xs"><EyeOff className="w-3 h-3" /> Oculto</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(m)} data-testid={`team-edit-${m.slug}`}
                      className="text-[#93C5FD] hover:text-white p-1" aria-label="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(m)} data-testid={`team-delete-${m.slug}`}
                      className="text-red-400 hover:text-red-300 p-1 ml-1" aria-label="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal member={editing} creating={creating} onClose={closeModal} onSaved={load} />
      )}
    </StaffLayout>
  );
}

function EditModal({ member, creating, onClose, onSaved }) {
  const [form, setForm] = useState(member);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (creating) {
        await api.post("/admin/team-members", form);
        toast.success("Miembro creado");
      } else {
        await api.put(`/admin/team-members/${member.id}`, form);
        toast.success("Cambios guardados");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (creating) {
      toast.info("Guarda primero al miembro y luego sube la foto");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/team-members/${member.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      upd("photo_url", data.photo_url);
      toast.success("Foto subida");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
        data-testid="team-edit-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">
              {creating ? "Nuevo miembro" : "Editar miembro"}
            </p>
            <h3 className="mt-1 text-lg font-bold text-white">{form.name || "Sin nombre"}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-gray-400">
            <X />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <F label="Nombre completo *" v={form.name} on={(v) => upd("name", v)} testid="tm-name" required />
          <F label="Puesto *" v={form.role_title} on={(v) => upd("role_title", v)} testid="tm-role" required />
          <div>
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Categoría *</span>
            <select value={form.role_category} onChange={(e) => upd("role_category", e.target.value)}
              data-testid="tm-category"
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1.5 text-sm text-white">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <F label="Orden (menor = arriba)" type="number" v={form.order} on={(v) => upd("order", parseInt(v || "0", 10))} testid="tm-order" />
          <F label="Color inicial (hex)" v={form.initials_color_hex} on={(v) => upd("initials_color_hex", v)} testid="tm-color" />
          <F label="LinkedIn URL" v={form.linkedin_url} on={(v) => upd("linkedin_url", v)} testid="tm-linkedin" />
        </div>

        <div>
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Bio corta * (max 1000 chars)</span>
          <textarea value={form.short_bio} onChange={(e) => upd("short_bio", e.target.value)} required
            rows={3} maxLength={1000}
            data-testid="tm-short-bio"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1.5 text-sm text-white" />
          <p className="text-[9px] text-gray-500 mt-0.5">{form.short_bio?.length || 0}/1000</p>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Bio completa (opcional)</span>
          <textarea value={form.full_bio} onChange={(e) => upd("full_bio", e.target.value)}
            rows={6}
            data-testid="tm-full-bio"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1.5 text-sm text-white" />
        </div>

        <div className="p-3 bg-[#0B0F14] border border-gray-800 rounded space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Foto</p>
          {form.photo_url ? (
            <img src={form.photo_url} alt="preview" className="w-24 h-24 rounded-full object-cover border border-gray-800" />
          ) : (
            <p className="text-xs text-gray-500">Sin foto — se mostrarán iniciales.</p>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || creating}
            data-testid="tm-upload-photo"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-white/5 disabled:opacity-40">
            <Upload className="w-3 h-3" /> {uploading ? "Subiendo..." : "Subir foto (max 3MB)"}
          </button>
          {creating && <p className="text-[10px] text-amber-300">Guarda primero y luego sube la foto.</p>}
        </div>

        <div className="p-3 bg-[#0B0F14] border border-red-500/20 rounded space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">Datos internos (no públicos)</p>
          <div className="grid md:grid-cols-2 gap-3">
            <F label="Email interno" v={form.internal_email} on={(v) => upd("internal_email", v)} testid="tm-internal-email" />
            <F label="Teléfono interno" v={form.internal_phone} on={(v) => upd("internal_phone", v)} testid="tm-internal-phone" />
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.is_public} onChange={(e) => upd("is_public", e.target.checked)}
              data-testid="tm-is-public" className="accent-[#F26A21]" />
            Público en /equipo
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.needs_bio_update} onChange={(e) => upd("needs_bio_update", e.target.checked)}
              data-testid="tm-needs-update" className="accent-amber-500" />
            Bio pendiente de actualizar
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-gray-800">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-gray-700 text-gray-200">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="tm-save-btn"
            className="text-xs px-3 py-1.5 rounded bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40">
            {saving ? "Guardando..." : creating ? "Crear" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, v, on, testid, required, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</span>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded px-2 py-1.5 text-sm text-white" />
    </label>
  );
}
